/**
 * FormStyler Component
 * Apple-quality design with true black dark mode
 */

import React, { useRef } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase';
import toast from 'react-hot-toast';

const FONTS = [
  { id: 'inter', name: 'Inter' },
  { id: 'sf-pro', name: 'SF Pro' },
  { id: 'helvetica', name: 'Helvetica' },
  { id: 'georgia', name: 'Georgia' },
  { id: 'playfair', name: 'Playfair' },
  { id: 'roboto', name: 'Roboto' },
  { id: 'lato', name: 'Lato' },
  { id: 'montserrat', name: 'Montserrat' }
];

const PRESET_COLORS = [
  '#FFB27D',
  '#000000',
  '#FFFFFF',
  '#3B82F6',
  '#10B981',
  '#8B5CF6',
  '#EC4899',
  '#F59E0B'
];

const FormStyler = ({ styling, onChange, userId }) => {
  const logoInputRef = useRef(null);
  const headerInputRef = useRef(null);

  const handleChange = (field, value) => {
    onChange({ ...styling, [field]: value });
  };

  const handleImageUpload = async (file, type) => {
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    try {
      const timestamp = Date.now();
      const path = `intake-forms/${userId}/${type}-${timestamp}-${file.name}`;
      const storageRef = ref(storage, path);

      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);

      handleChange(type === 'logo' ? 'logo' : 'headerImage', url);
      toast.success('Uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Upload failed');
    }
  };

  const ColorPicker = ({ label, value, field }) => (
    <div>
      <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <div className="relative">
          <input
            type="color"
            value={value || '#FFB27D'}
            onChange={(e) => handleChange(field, e.target.value)}
            className="w-10 h-10 rounded-lg border border-black/10 dark:border-white/10 cursor-pointer overflow-hidden"
          />
        </div>
        <input
          type="text"
          value={value || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder="#FFB27D"
          className="flex-1 px-3 py-2 rounded-lg bg-black/[0.03] dark:bg-white/5
            border border-black/5 dark:border-white/10 text-black dark:text-white text-[13px] font-mono
            placeholder:text-black/30 dark:placeholder:text-white/30
            focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
        />
      </div>
      <div className="flex gap-1.5 mt-2">
        {PRESET_COLORS.map((color) => (
          <button
            key={color}
            onClick={() => handleChange(field, color)}
            className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${
              value === color ? 'border-black dark:border-white scale-110' : 'border-black/10 dark:border-white/10'
            }`}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Colors */}
      <div>
        <h3 className="text-[15px] font-semibold text-black dark:text-white mb-4">Colors</h3>
        <div className="grid grid-cols-2 gap-4">
          <ColorPicker label="Primary" value={styling?.primaryColor} field="primaryColor" />
          <ColorPicker label="Background" value={styling?.backgroundColor} field="backgroundColor" />
          <ColorPicker label="Text" value={styling?.textColor} field="textColor" />
          <ColorPicker label="Secondary" value={styling?.secondaryColor} field="secondaryColor" />
        </div>
      </div>

      {/* Fonts */}
      <div>
        <h3 className="text-[15px] font-semibold text-black dark:text-white mb-4">Typography</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
              Heading
            </label>
            <select
              value={styling?.headingFont || 'inter'}
              onChange={(e) => handleChange('headingFont', e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
            >
              {FONTS.map(font => (
                <option key={font.id} value={font.id}>{font.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
              Body
            </label>
            <select
              value={styling?.bodyFont || 'inter'}
              onChange={(e) => handleChange('bodyFont', e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-black/[0.03] dark:bg-white/5
                border border-black/5 dark:border-white/10 text-black dark:text-white text-[15px]
                focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50"
            >
              {FONTS.map(font => (
                <option key={font.id} value={font.id}>{font.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Images */}
      <div>
        <h3 className="text-[15px] font-semibold text-black dark:text-white mb-4">Images</h3>

        {/* Logo */}
        <div className="mb-4">
          <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
            Logo
          </label>
          {styling?.logo ? (
            <div className="relative inline-block">
              <img
                src={styling.logo}
                alt="Logo"
                className="h-14 w-auto rounded-lg border border-black/10 dark:border-white/10"
              />
              <button
                onClick={() => handleChange('logo', null)}
                className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full shadow-sm hover:bg-red-600"
              >
                <XMarkIcon className="w-3 h-3" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => logoInputRef.current?.click()}
              className="w-full py-6 border-2 border-dashed border-black/10 dark:border-white/10 rounded-xl
                hover:border-brand/50 hover:bg-brand/5 transition-colors
                flex flex-col items-center gap-1.5 text-black/30 dark:text-white/30"
            >
              <PhotoIcon className="w-6 h-6" />
              <span className="text-[13px]">Upload logo</span>
            </button>
          )}
          <input
            ref={logoInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e.target.files[0], 'logo')}
            className="hidden"
          />
        </div>

        {/* Header */}
        <div>
          <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
            Header Image
          </label>
          {styling?.headerImage ? (
            <div className="relative">
              <img
                src={styling.headerImage}
                alt="Header"
                className="w-full h-28 object-cover rounded-xl border border-black/10 dark:border-white/10"
              />
              <button
                onClick={() => handleChange('headerImage', null)}
                className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-sm hover:bg-red-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => headerInputRef.current?.click()}
              className="w-full py-6 border-2 border-dashed border-black/10 dark:border-white/10 rounded-xl
                hover:border-brand/50 hover:bg-brand/5 transition-colors
                flex flex-col items-center gap-1.5 text-black/30 dark:text-white/30"
            >
              <PhotoIcon className="w-6 h-6" />
              <span className="text-[13px]">Upload header</span>
            </button>
          )}
          <input
            ref={headerInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleImageUpload(e.target.files[0], 'header')}
            className="hidden"
          />
        </div>
      </div>

      {/* Button Style */}
      <div>
        <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
          Button Style
        </label>
        <div className="flex gap-2">
          {[
            { id: 'rounded', label: 'Rounded', className: 'rounded-xl' },
            { id: 'pill', label: 'Pill', className: 'rounded-full' },
            { id: 'square', label: 'Square', className: 'rounded-none' }
          ].map((style) => (
            <button
              key={style.id}
              onClick={() => handleChange('buttonStyle', style.id)}
              className={`flex-1 py-2.5 px-4 border-2 text-[14px] font-medium transition-all ${style.className} ${
                styling?.buttonStyle === style.id
                  ? 'border-brand bg-brand/10 text-brand'
                  : 'border-black/10 dark:border-white/10 text-black/50 dark:text-white/50 hover:border-black/20 dark:hover:border-white/20'
              }`}
            >
              {style.label}
            </button>
          ))}
        </div>
      </div>

      {/* Form Width */}
      <div>
        <label className="block text-[13px] font-medium text-black/50 dark:text-white/50 mb-2">
          Form Width
        </label>
        <div className="flex gap-2">
          {[
            { id: 'narrow', label: 'Narrow' },
            { id: 'medium', label: 'Medium' },
            { id: 'wide', label: 'Wide' }
          ].map((width) => (
            <button
              key={width.id}
              onClick={() => handleChange('formWidth', width.id)}
              className={`flex-1 py-2.5 px-4 rounded-xl border-2 text-[14px] font-medium transition-all ${
                styling?.formWidth === width.id
                  ? 'border-brand bg-brand/10 text-brand'
                  : 'border-black/10 dark:border-white/10 text-black/50 dark:text-white/50 hover:border-black/20 dark:hover:border-white/20'
              }`}
            >
              {width.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FormStyler;
